import React, { useEffect, useState } from "react";
import { DeleteOutlined, ExclamationCircleOutlined, PlusOutlined } from "@ant-design/icons";
import User from "../../services/api/User";
import { Col, Modal, Row, Select, Switch, Table } from "antd";
import { useTranslation } from "react-i18next";
import { useFormik } from "formik";
import { showError, showSuccess } from "../../utils/utilities";
import { get } from "lodash";
import Button from "../functional_components/Button/PFButton";

const SalSubstituteComponent = ({ username }) => {
  const { confirm } = Modal;
  const { t } = useTranslation();
  const formikSalSub = useFormik({
    initialValues: {
      supplierUsername: null,
      projectPhaseId: null,
      projectTaskId: null,
      enabled: true,
    },
    enableReinitialize: true,
    onSubmit: (values) => {
      const dataSave = {
        enabled: values.enabled,
        wbsCategory: values.projectPhaseId
          ? {
              id: values.projectPhaseId,
            }
          : undefined,
        wbs: values.projectTaskId
          ? {
              id: values.projectTaskId,
            }
          : undefined,
        supplierUsername: values.supplierUsername,
      };
      editRecord(dataSave);
    },
  });
  const [tableLoader, setTableLoader] = useState(false);
  const [formLoader, setFormLoader] = useState(false);
  const [salData, setSalData] = useState([]);
  const [optionData, setOptionData] = useState({
    suppliers: [],
    phases: [],
    tasks: [],
  });
  useEffect(() => {
    const fetchSuppliers = async () => {
      const { data, error } = await User.getSalSubstituteSuppliers();
      if (!error) {
        setOptionData({ ...optionData, suppliers: data });
      } else {
        showError(get(error, "messageCode", "ER000"));
      }
    };
    // Recupero dati esistenti
    fetchData();
    // Recupero subito la lista dei suppliers, gli altri andranno a cascata
    fetchSuppliers();
  }, []);
  const fetchData = async () => {
    setTableLoader(true);
    const { data, error } = await User.getSalSubstituteAbilitations(username);
    if (!error) {
      setSalData(data);
    } else {
      showError(get(error, "messageCode", "ER000"));
    }
    setTableLoader(false);
  };
  const fetchPhases = async (supplierUsername) => {
    setFormLoader(true);
    const { data, error } = await User.getSalSubstitutePhases(supplierUsername);
    if (!error) {
      setOptionData({ ...optionData, phases: data });
    } else {
      showError(get(error, "messageCode", "ER000"));
    }
    setFormLoader(false);
  };
  const fetchTasks = async (supplierUsername, projectPhaseId) => {
    setFormLoader(true);
    const { data, error } = await User.getSalSubstituteTasks(supplierUsername, projectPhaseId);
    if (!error) {
      setOptionData({ ...optionData, tasks: data });
    } else {
      showError(get(error, "messageCode", "ER000"));
    }
    setFormLoader(false);
  };
  const editRecord = async (data) => {
    setTableLoader(true);
    const { error } = await User.setSalSubstituteAbilitations(username, data);
    if (!error) {
      showSuccess();
      fetchData();
      formikSalSub.resetForm();
    } else {
      showError(get(error, "messageCode", "ER000"));
      setTableLoader(false); // lo metto qui, perchè se la chiamata va a buon fine, il loader lo ferma alla fetchData
    }
  };
  const deleteRecord = async (id) => {
    confirm({
      title: t("general.ensureDelete"),
      icon: <ExclamationCircleOutlined />,
      onOk: async () => {
        setTableLoader(true);
        const { error } = await User.deleteSalSubstituteAbilitations(username, id);
        if (!error) {
          fetchData();
        } else {
          showError(get(error, "messageCode", "ER000"));
        }
        setTableLoader(false);
      },
      onCancel() {},
    });
  };
  return (
    <>
      <Row gutter={[48, 32]} className="mt-2em">
        <Col span={24}>
          <Table
            loading={tableLoader}
            pagination={false}
            rowKey="id"
            columns={[
              {
                title: t("salSubstituteComponent.supplierUsername"),
                dataIndex: "supplierName",
                key: "supplierName",
              },
              {
                title: t("salSubstituteComponent.projectPhaseId"),
                dataIndex: "wbsCategory",
                key: "wbsCategory",
                render: (wbsCategory) => (wbsCategory ? wbsCategory.id + " | " + wbsCategory.name : "-"),
              },
              {
                title: t("salSubstituteComponent.projectTaskId"),
                dataIndex: "wbs",
                key: "wbs",
                render: (wbs) => (wbs ? wbs.id + " | " + wbs.name : "-"),
              },
              {
                title: t("salSubstituteComponent.enabled"),
                dataIndex: "enabled",
                key: "enabled",
                render: (enabled, record) => (
                  <Switch
                    onChange={(val) =>
                      editRecord({
                        ...record,
                        projectPhase: record.projectPhase ? record.projectPhase : undefined,
                        projectTask: record.projectTask ? record.projectTask : undefined,
                        enabled: val,
                      })
                    }
                    checked={enabled}
                  />
                ),
              },
              {
                title: "",
                key: "action",
                render: (_, record) => (
                  <div className="space-around">
                    <div onClick={() => (record.deletable ? deleteRecord(record.id) : {})}>
                      <DeleteOutlined className={`icon-small ${!record.deletable ? "disabled no-pointer" : ""}`} />
                    </div>
                  </div>
                ),
              },
            ]}
            dataSource={salData}
          />
        </Col>
      </Row>
      <Row gutter={[48, 32]} className="mt-2em">
        <Col lg={7} md={7} xs={24} sm={24}>
          <Row>
            <label className="fw-bold mt-10" htmlFor="supplierUsername">
              {t("salSubstituteComponent.supplierUsername")}
            </label>
          </Row>
          <Row>
            <Select
              allowClear
              className="w-100"
              id="supplierUsername"
              name="supplierUsername"
              showSearch
              optionFilterProp="label"
              onChange={(supplierUsername) => {
                formikSalSub.setFieldValue("projectTaskId", null);
                formikSalSub.setFieldValue("projectPhaseId", null);
                formikSalSub.setFieldValue("supplierUsername", supplierUsername);
                // popolo le options della select di "projectPhaseId"
                fetchPhases(supplierUsername);
              }}
              placeholder={t("salSubstituteComponent.supplierUsername")}
              onBlur={formikSalSub.handleBlur("supplierUsername")}
              value={formikSalSub.values.supplierUsername}
              options={optionData.suppliers.map((s) => ({ value: s.username, label: s.name }))}
            />
          </Row>
        </Col>
        <Col lg={7} md={7} xs={24} sm={24}>
          <Row>
            <label className="fw-bold mt-10" htmlFor="projectPhaseId">
              {t("salSubstituteComponent.projectPhaseId")}
            </label>
          </Row>
          <Row>
            <Select
              loading={formLoader}
              disabled={formLoader || !formikSalSub.values.supplierUsername}
              className="w-100"
              id="projectPhaseId"
              name="projectPhaseId"
              showSearch
              optionFilterProp="label"
              onChange={(projectPhaseId) => {
                formikSalSub.setFieldValue("projectTaskId", null);
                formikSalSub.setFieldValue("projectPhaseId", projectPhaseId);
                // popolo le options della select di "projectPhaseId"
                fetchTasks(formikSalSub.values.supplierUsername, projectPhaseId);
              }}
              placeholder={t("salSubstituteComponent.projectPhaseId")}
              onBlur={formikSalSub.handleBlur("projectPhaseId")}
              value={formikSalSub.values.projectPhaseId}
              options={optionData.phases.map((s) => ({ value: s.id, label: `${s.id} | ${s.name}` }))}
            />
          </Row>
        </Col>
        <Col lg={7} md={7} xs={24} sm={24}>
          <Row>
            <label className="fw-bold mt-10" htmlFor="projectTaskId">
              {t("salSubstituteComponent.projectTaskId")}
            </label>
          </Row>
          <Row>
            <Select
              loading={formLoader}
              disabled={formLoader || !formikSalSub.values.projectPhaseId}
              className="w-100"
              id="projectTaskId"
              name="projectTaskId"
              showSearch
              optionFilterProp="label"
              onChange={formikSalSub.handleChange("projectTaskId")}
              placeholder={t("salSubstituteComponent.projectTaskId")}
              onBlur={formikSalSub.handleBlur("projectTaskId")}
              value={formikSalSub.values.projectTaskId}
              options={optionData.tasks.map((s) => ({ value: s.id, label: `${s.id} | ${s.name}` }))}
            />
          </Row>
        </Col>
        <Col lg={3} md={3} xs={24} sm={24}>
          <Row>
            <label className="fw-bold mt-10" htmlFor="enabled">
              {t("salSubstituteComponent.enabled")}
            </label>
          </Row>
          <Row>
            <Switch
              id="enabled"
              name="enabled"
              onChange={() => {
                formikSalSub.setFieldValue("enabled", !formikSalSub.values.enabled);
              }}
              checked={formikSalSub.values.enabled}
            />
          </Row>
        </Col>
      </Row>
      <Row gutter={16} type="flex" align="middle" justify="end" className="mt-10">
        <Col>{formikSalSub.values.supplierUsername ? t("salSubstituteComponent.infoText") : ""}</Col>
        <Col>
          <Button
            currentSize="small"
            isLoading={formLoader}
            isDisabled={!formikSalSub.values.supplierUsername}
            clickCallback={formikSalSub.submitForm}
            currentIcon={<PlusOutlined className="icon-small" />}
            content={t("salSubstituteComponent.insert")}
          />
        </Col>
      </Row>
    </>
  );
};

export default SalSubstituteComponent;
